import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import CodePicker from '../Components/MarketplaceSettings/CodePicker';
import { useAuth0 } from '@auth0/auth0-react';
import debounce from 'lodash/debounce';
import { useToast } from '../Context/ToastContext';
import { fetchTaxonomy, findCategory, groupCategories, sanitizeSubcategories } from '../Data/advisoryTaxonomy';

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 93vh;
  background-color: #0f1419;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: hidden;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  background-color: #0f1419;
  color: #FFFFFF;
  padding: 20px;
  box-sizing: border-box;
  position: relative;
  overflow-y: auto;
  border-radius: 10px;
`;

const FormNote = styled.div`
  background-color: rgba(120, 130, 140, 0.1);
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  
  &:before {
    content: '⚠️';
    margin-right: 10px;
    font-size: 18px;
  }
`;

const Section = styled.div`
  background-color: rgba(120, 130, 140, 0.05);
  border: none;
  border-radius: 4px;
  border-left: 4px solid rgba(120, 130, 140, 0.5);
  padding: 20px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: rgba(160, 170, 180, 0.9);
  font-size: 24px;
  margin-bottom: 20px;
  padding: 8px 16px;
  display: inline-block;
  border: 1px solid rgba(120, 130, 140, 0.5);
  border-radius: 4px;
  background-color: rgba(120, 130, 140, 0.05);
`;

const InputGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
`;

const Input = styled.input`
  width: 100%;
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.9) 0%, rgba(30, 35, 50, 0.9) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  padding: 12px;
  color: #FFFFFF;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.9) 0%, rgba(30, 35, 50, 0.9) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  padding: 12px;
  color: #FFFFFF;
  min-height: 100px;
  resize: vertical;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const FileUpload = styled.div`
  border: 2px dashed rgba(120, 130, 140, 0.5);
  border-radius: 4px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  margin-top: 10px;
  position: relative;
  background-color: rgba(120, 130, 140, 0.02);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(120, 130, 140, 0.05);
    border-color: rgba(120, 130, 140, 0.8);
  }

  input[type="file"] {
    opacity: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    cursor: pointer;
  }

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;

    &:before {
      content: '📎';
      font-size: 20px;
    }
  }
`;

const FilePreview = styled.div`
  margin-top: 10px;
  padding: 10px;
  background-color: rgba(120, 130, 140, 0.05);
  border-radius: 4px;
  
  .file-name {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    background-color: rgba(120, 130, 140, 0.1);
    border-radius: 4px;
    margin-bottom: 5px;
    
    span {
      color: rgba(255, 255, 255, 0.8);
      font-size: 14px;
    }
    
    button {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.6);
      cursor: pointer;
      padding: 4px 8px;
      
      &:hover {
        color: rgba(255, 255, 255, 0.9);
      }
    }
  }
`;

const FILE_RESTRICTIONS = {
  MAX_FILES: 10,
  MAX_SIZE: 10 * 1024 * 1024, // 10MB in bytes
  ACCEPTED_FORMATS: ".jpg,.jpeg,.png,.gif,.bmp,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.html,.md,.csv,.json,.epub"
};

const OperatingHoursGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: rgba(120, 130, 140, 0.02);
  padding: 16px;
  border-radius: 4px;
`;

const TimeSelectionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
`;

const DaysSelectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 10px;
`;

const DayButton = styled.button`
  background: ${props => props.isSelected ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.4) 0%, rgba(129, 140, 248, 0.4) 100%)' : 'linear-gradient(135deg, rgba(25, 30, 45, 0.9) 0%, rgba(30, 35, 50, 0.9) 100%)'};
  color: #FFFFFF;
  border: 1px solid ${props => props.isSelected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.isSelected ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.5) 0%, rgba(129, 140, 248, 0.5) 100%)' : 'rgba(99, 102, 241, 0.1)'};
    border-color: rgba(99, 102, 241, 0.4);
  }
`;

const TimeInput = styled.input`
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.9) 0%, rgba(30, 35, 50, 0.9) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  padding: 8px;
  color: #FFFFFF;
  width: 100%;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  
  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const DurationSettings = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: rgba(120, 130, 140, 0.02);
  border-radius: 4px;
`;

const DurationRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  width: 100%;
  background: linear-gradient(135deg, rgba(25, 30, 45, 0.9) 0%, rgba(30, 35, 50, 0.9) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 6px;
  padding: 12px;
  color: #FFFFFF;
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
  
  option {
    background-color: #1a1f2e;
  }
`;

const DurationSelect = styled(Select)`
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: #ff4d4d;
  font-size: 12px;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  color: #4dff4d;
  font-size: 12px;
  margin-top: 4px;
`;

const Required = styled.span`
  color: #ff4d4d;
  margin-left: 4px;
`;

const SubmitButton = styled.button`
  width: 100%;
  background-color: rgba(120, 130, 140, 0.5);
  color: #FFFFFF;
  border: none;
  border-radius: 4px;
  padding: 16px;
  font-size: 16px;
  cursor: pointer;
  margin-top: 20px;
  
  &:hover {
    background-color: rgba(120, 130, 140, 0.7);
  }
  
  &:disabled {
    background-color: rgba(99, 102, 241, 0.25);
    cursor: not-allowed;
  }
`;

const MainRequirementTextArea = styled(TextArea)`
  user-select: text;
  cursor: text;
  overflow-y: auto;
  background-color: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.25) !important;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.7) !important;
  margin: 10px 20px;
  line-height: 1.6;
  font-size: 14px;
`;

// Category + subcategory options now come from the server-authoritative taxonomy
// (see src/Data/advisoryTaxonomy.js -> GET /onboarding/taxonomy). No hardcoded list.

// Chip-style toggle for picking one or more subcategories.
const SubcategoryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const SubChip = styled.button`
  border: 1px solid ${(p) => (p.selected ? '#2563eb' : '#d1d5db')};
  background: ${(p) => (p.selected ? '#2563eb' : '#fff')};
  color: ${(p) => (p.selected ? '#fff' : '#374151')};
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: #2563eb; }
`;

// Color scheme choices for the chat widget
const COLOR_SCHEME_OPTIONS = [
  'cyan',
  'darkblue',
  'lightblue', // default
  'red',
  'orange',
  'magenta',
  'green',
  'yellow'
];

const OnboardingForm = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    generalInfo: {
      businessName: '',
      location: '',
      email: '',
      website: '',
      phoneNumber: '',
    },
    socialMedia: {
      linkedin: '',
      facebook: '',
      twitter: '',
      instagram: ''
    },
    mainContact: {
      name: '',
      position: '',
      email: '',
      phone: ''
    },
    businessDetails: {
      operatingHours: '',
      industry: '',
      greetingMessage: 'Hello! How can I assist you today?'
    },
    additionalInfo: {
      interactionVolume: '',
      notes: ''
    },
    uniqueName: '',
    category: '',
    subcategories: [],
    jurisdiction: '',
    widgetCustomization: {
      headerColor: 'lightblue',
      logoUrl: 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg'
    },
    // Optional public-marketplace details (imavenai.com). Completed later in
    // Settings → Marketplace if skipped here. Codes only: ISO 639-1 / ISO 3166-1.
    marketplace: {
      listed: true,
      languages: [],
      countries: [],
      firstConsultation: { free: false, price: '', currency: 'USD' }
    },
    // 'adviser' works with clients; 'creator' is here to build and publish an AI agent.
    accountType: 'adviser'
  });

  const [operatingHours, setOperatingHours] = useState({
    workingDays: {
      sunday: false,    // 0
      monday: true,     // 1
      tuesday: true,    // 2
      wednesday: true,  // 3
      thursday: true,   // 4
      friday: true,     // 5
      saturday: false   // 6
    },
    hours: {
      start: '09:00',
      end: '17:00'
    }
  });

  const [meetingDuration, setMeetingDuration] = useState({
    minDuration: '15',
    maxDuration: '60'
  });

  const [uniqueNameStatus, setUniqueNameStatus] = useState({
    isChecking: false,
    isAvailable: false,
    message: ''
  });

  const [taxonomy, setTaxonomy] = useState([]);
  const [mpOptions, setMpOptions] = useState({ languages: [], countries: [], currencies: [] });

  // Expert Terms (agent data responsibility). The text and version come from the server
  // (GET /onboarding/terms); acceptance is kept out of formData on purpose so the saved
  // draft never pre-ticks it — accepting must be a deliberate act on every submit.
  const [terms, setTerms] = useState(null);
  const [termsError, setTermsError] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadTerms = () => {
    setTermsError(false);
    return axios.get(`${process.env.REACT_APP_SERVER}onboarding/terms`)
      .then(res => setTerms(res.data))
      .catch(() => setTermsError(true));
  };
  useEffect(() => { loadTerms(); }, []);

  // Marketplace vocabularies (languages / countries / currencies) — server is the source of truth.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_SERVER}marketplace/options`)
      .then(res => { if (!cancelled) setMpOptions({
        languages: res.data?.languages || [], countries: res.data?.countries || [], currencies: res.data?.currencies || []
      }); })
      .catch(() => { /* optional section; the picker just stays empty */ });
    return () => { cancelled = true; };
  }, []);

  const setMarketplace = (patch) => setFormData(prev => ({
    ...prev,
    marketplace: { ...(prev.marketplace || {}), ...patch }
  }));
  const setMarketplaceFc = (patch) => setFormData(prev => ({
    ...prev,
    marketplace: {
      ...(prev.marketplace || {}),
      firstConsultation: { ...((prev.marketplace && prev.marketplace.firstConsultation) || {}), ...patch }
    }
  }));

  useEffect(() => {
    const savedData = localStorage.getItem('onboardingFormDraft');
    if (savedData) {
      setFormData(prev => ({ ...prev, ...JSON.parse(savedData) }));
    }
  }, []);

  // Load the server-authoritative category/subcategory catalog. Default the
  // category to the first one only if the expert hasn't already chosen (or restored).
  useEffect(() => {
    let cancelled = false;
    fetchTaxonomy().then((cats) => {
      if (cancelled) return;
      setTaxonomy(cats);
      setFormData(prev => (prev.category ? prev : { ...prev, category: cats[0]?.label || '' }));
    });
    return () => { cancelled = true; };
  }, []);

  const selectedCategory = findCategory(taxonomy, formData.category);

  // When the category changes, drop any now-invalid subcategory selections.
  const handleCategoryChange = (label) => {
    const cat = findCategory(taxonomy, label);
    setFormData(prev => ({
      ...prev,
      category: label,
      subcategories: sanitizeSubcategories(cat, prev.subcategories),
    }));
  };

  const toggleSubcategory = (slug) => {
    setFormData(prev => {
      const multi = selectedCategory?.subMulti !== false;
      const has = prev.subcategories.includes(slug);
      let next;
      if (multi) {
        next = has ? prev.subcategories.filter(s => s !== slug) : [...prev.subcategories, slug];
      } else {
        next = has ? [] : [slug];
      }
      return { ...prev, subcategories: next };
    });
  };

  useEffect(() => {
    localStorage.setItem('onboardingFormDraft', JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (section, field, value) => {
    if (section === 'uniqueName') {
      const sanitizedValue = value ? value.replace(/[^a-zA-Z0-9-_]/g, '-').replace(/\s+/g, '-') : '';
      setFormData(prev => ({
        ...prev,
        uniqueName: sanitizedValue
      }));

      if (!sanitizedValue) {
        setUniqueNameStatus({
          isChecking: false,
          isAvailable: false,
          message: ''
        });
      }
    } else if (section === 'category') {
      setFormData(prev => ({
        ...prev,
        category: value
      }));
    } else if (section === 'jurisdiction') {
      setFormData(prev => ({
        ...prev,
        jurisdiction: value
      }));
    } else if (section === 'widgetCustomization') {
      setFormData(prev => ({
        ...prev,
        widgetCustomization: {
          ...prev.widgetCustomization,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
  };

  const handleOperatingHoursChange = (type, value) => {
    if (type === 'day') {
      setOperatingHours(prev => ({
        ...prev,
        workingDays: {
          ...prev.workingDays,
          [value]: !prev.workingDays[value]
        }
      }));
    } else {
      setOperatingHours(prev => ({
        ...prev,
        hours: {
          ...prev.hours,
          [type]: value
        }
      }));
    }

    const workingDaysStr = Object.entries(operatingHours.workingDays)
      .filter(([_, isWorking]) => isWorking)
      .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1))
      .join(', ');

    const hoursString = `Working Days: ${workingDaysStr}\nHours: ${operatingHours.hours.start} - ${operatingHours.hours.end}`;
    handleInputChange('businessDetails', 'operatingHours', hoursString);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!uniqueNameStatus.isAvailable) {
      showToast('Please choose an available unique name before submitting.', 'warning');
      return;
    }

    if (!formData.category) {
      showToast('Please select a category.', 'warning');
      return;
    }

    if (selectedCategory?.subRequired && formData.subcategories.length === 0) {
      showToast(`Please select at least one ${selectedCategory.subLabel.toLowerCase()}.`, 'warning');
      return;
    }

    if (!terms || !termsAccepted) {
      showToast('Please read and accept the Expert Terms.', 'warning');
      return;
    }

    const workingDayIds = Object.entries(operatingHours.workingDays)
      .map(([day, isWorking], index) => ({ day, id: getDayId(day), isWorking }))
      .filter(({ isWorking }) => isWorking)
      .map(({ id }) => id);

    const requiredFields = {
      generalInfo: {
        businessName: formData.generalInfo.businessName,
        location: formData.generalInfo.location,
        email: formData.generalInfo.email,
        website: formData.generalInfo.website,
        phoneNumber: formData.generalInfo.phoneNumber,
      },
      socialMedia: {
        linkedin: formData.socialMedia.linkedin,
        facebook: formData.socialMedia.facebook,
        twitter: formData.socialMedia.twitter,
        instagram: formData.socialMedia.instagram
      },
      businessDetails: {
        industry: formData.businessDetails.industry,
        operatingHours: {
          workingDays: workingDayIds,
          hours: operatingHours.hours
        },
        greetingMessage: formData.businessDetails.greetingMessage
      },
      additionalInfo: {
        interactionVolume: formData.additionalInfo.interactionVolume,
        notes: formData.additionalInfo.notes
      },
      uniqueName: formData.uniqueName,
      userCategories: formData.category,
      subcategories: formData.subcategories,
      // For immigration the primary country doubles as the jurisdiction the backend
      // uses for IRCC/brain routing; the server re-derives this too (defensive).
      jurisdiction: /immigration|visa|migration|asylum|refugee/i.test(formData.category)
        ? (formData.subcategories[0] || 'canada')
        : undefined,
      widgetCustomization: {
        headerColor: formData.widgetCustomization.headerColor || 'lightblue',
        logoUrl: formData.widgetCustomization.logoUrl || 'https://ststic-imaven-images.nyc3.cdn.digitaloceanspaces.com/bluelogo.svg'
      },
      marketplace: formData.marketplace || undefined,
      accountType: formData.accountType || 'adviser',
      termsAccepted: termsAccepted === true,
      termsVersion: terms?.version
    };

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();

      const response = await axios.post(`${process.env.REACT_APP_SERVER}onboarding`, requiredFields, {
        headers: {
          Authorization: `Bearer ${token}`
        },
      });
      if (response.status === 201) {
        localStorage.removeItem('onboardingFormDraft');
        window.location.href = '/';
      } else {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const data = error?.response?.data;
      // The terms changed while the form was open: show the new text and ask again.
      if (data?.terms && (data.code === 'terms-outdated' || data.code === 'terms-required')) {
        setTerms({ version: data.terms.version, title: data.terms.title, text: data.terms.text });
        setTermsAccepted(false);
      }
      showToast(data?.message || 'Could not submit the onboarding form. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getDayId = (day) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return days.indexOf(day.toLowerCase());
  };

  const checkNameOnBlur = () => {
    const name = formData.uniqueName;
    if (!name) {
      setUniqueNameStatus({
        isChecking: false,
        isAvailable: false,
        message: ''
      });
      return;
    }

    setUniqueNameStatus(prev => ({
      ...prev,
      isChecking: true,
      message: 'Checking availability...'
    }));

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER}onboarding/checkUniqueName/${name}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        setUniqueNameStatus({
          isChecking: false,
          isAvailable: response.data.isAvailable,
          message: response.data.isAvailable ? 'This name is available!' : 'This name is already taken.'
        });
      } catch (error) {
        setUniqueNameStatus({
          isChecking: false,
          isAvailable: false,
          message: 'Error checking name availability. Please try again.'
        });
      }
    })();
  };

  const checkFormValidity = () => {
    return (
      uniqueNameStatus.isAvailable &&
      formData.generalInfo.businessName?.trim() !== '' &&
      formData.generalInfo.location?.trim() !== '' &&
      formData.generalInfo.email?.trim() !== '' &&
      formData.generalInfo.phoneNumber?.trim() !== '' &&
      formData.uniqueName?.trim() !== '' &&
      formData.businessDetails.industry?.trim() !== '' &&
      formData.businessDetails.greetingMessage?.trim() !== '' &&
      formData.category?.trim() !== '' &&
      !!terms && termsAccepted
    );
  };

  return (
    <PageWrapper>
      <FormContainer>
        <FormNote>
          All fields are required unless otherwise indicated.
        </FormNote>
        <Section>
          <SectionTitle>What are you here for?</SectionTitle>
          <div style={{ display: 'grid', gap: '10px' }}>
            {[
              ['adviser', 'I advise clients', 'Take leads from the marketplace and my own links, run intakes, manage cases. I can also publish an AI agent later.'],
              ['creator', 'I want to publish an AI agent', 'Build an AI advisor on my knowledge and earn a royalty on every consultation. I do not take clients myself.'],
            ].map(([value, title, text]) => (
              <label key={value} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px 14px', borderRadius: '10px', border: formData.accountType === value ? '1px solid #2563eb' : '1px solid rgba(255,255,255,0.15)', cursor: 'pointer' }}>
                <input type="radio" name="accountType" value={value} checked={formData.accountType === value} onChange={() => setFormData(prev => ({ ...prev, accountType: value, marketplace: { ...(prev.marketplace || {}), listed: value === 'adviser' } }))} style={{ marginTop: '3px' }} />
                <span><strong>{title}</strong><span style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>{text}</span></span>
              </label>
            ))}
          </div>
        </Section>

        <Section>
          <SectionTitle>General Information</SectionTitle>
          <InputGroup>
            <Label>Business Name <Required>*</Required></Label>
            <Input
              type="text"
              value={formData.generalInfo.businessName}
              onChange={(e) => handleInputChange('generalInfo', 'businessName', e.target.value)}
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Location <Required>*</Required></Label>
            <Input
              type="text"
              value={formData.generalInfo.location}
              onChange={(e) => handleInputChange('generalInfo', 'location', e.target.value)}
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Email Address <Required>*</Required></Label>
            <Input
              type="email"
              value={formData.generalInfo.email}
              onChange={(e) => handleInputChange('generalInfo', 'email', e.target.value)}
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Website URL</Label>
            <Input
              type="url"
              value={formData.generalInfo.website}
              onChange={(e) => handleInputChange('generalInfo', 'website', e.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <Label>Phone Number <Required>*</Required></Label>
            <Input
              type="tel"
              value={formData.generalInfo.phoneNumber}
              onChange={(e) => handleInputChange('generalInfo', 'phoneNumber', e.target.value)}
              required
            />
          </InputGroup>
          <InputGroup>
            <Label>Category <Required>*</Required></Label>
            <Select
              value={formData.category}
              onChange={e => handleCategoryChange(e.target.value)}
              required
            >
              <option value="" disabled>Select a category…</option>
              {groupCategories(taxonomy).map(({ group, categories }) => (
                <optgroup key={group} label={group}>
                  {categories.map(cat => (
                    <option key={cat.slug} value={cat.label}>{cat.label}</option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </InputGroup>
          {selectedCategory && selectedCategory.subcategories?.length > 0 && (
            <InputGroup>
              <Label>
                {selectedCategory.subLabel}
                {selectedCategory.subRequired && <Required>*</Required>}
                {selectedCategory.subMulti && (
                  <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                    (select all that apply)
                  </span>
                )}
              </Label>
              <SubcategoryChips>
                {selectedCategory.subcategories.map(sub => (
                  <SubChip
                    type="button"
                    key={sub.slug}
                    selected={formData.subcategories.includes(sub.slug)}
                    onClick={() => toggleSubcategory(sub.slug)}
                  >
                    {sub.label}
                  </SubChip>
                ))}
              </SubcategoryChips>
            </InputGroup>
          )}

          <SectionTitle>Social Platforms</SectionTitle>
          <InputGroup>
            <Label>LinkedIn URL</Label>
            <Input
              type="url"
              value={formData.socialMedia.linkedin}
              onChange={(e) => handleInputChange('socialMedia', 'linkedin', e.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <Label>Facebook URL</Label>
            <Input
              type="url"
              value={formData.socialMedia.facebook}
              onChange={(e) => handleInputChange('socialMedia', 'facebook', e.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <Label>X (Twitter) URL</Label>
            <Input
              type="url"
              value={formData.socialMedia.twitter}
              onChange={(e) => handleInputChange('socialMedia', 'twitter', e.target.value)}
            />
          </InputGroup>
          <InputGroup>
            <Label>Instagram URL</Label>
            <Input
              type="url"
              value={formData.socialMedia.instagram}
              onChange={(e) => handleInputChange('socialMedia', 'instagram', e.target.value)}
            />
          </InputGroup>
        </Section>

        <Section>
          <SectionTitle>Business Details</SectionTitle>
          <InputGroup>
            <Label>Operating Hours</Label>
            <OperatingHoursGrid>
              <TimeSelectionRow>
                <TimeInput
                  type="time"
                  value={operatingHours.hours.start}
                  onChange={(e) => handleOperatingHoursChange('start', e.target.value)}
                  required
                />
                <span style={{ color: 'white' }}>to</span>
                <TimeInput
                  type="time"
                  value={operatingHours.hours.end}
                  onChange={(e) => handleOperatingHoursChange('end', e.target.value)}
                  required
                />
              </TimeSelectionRow>
              <Label>Select Working Days</Label>
              <DaysSelectionGrid>
                {Object.entries(operatingHours.workingDays).map(([day, isSelected]) => (
                  <DayButton
                    key={day}
                    isSelected={isSelected}
                    onClick={() => handleOperatingHoursChange('day', day)}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </DayButton>
                ))}
              </DaysSelectionGrid>
            </OperatingHoursGrid>

            <InputGroup>
              <Label>Company About <Required>*</Required></Label>
              <TextArea
                value={formData.businessDetails.industry}
                onChange={(e) => handleInputChange('businessDetails', 'industry', e.target.value)}
                placeholder="Tell us about your company. This information will be visible in your widget profile."
                required
              />
            </InputGroup>
            <InputGroup>
              <Label>AI Assistant Greeting Message <Required>*</Required></Label>
              <TextArea
                value={formData.businessDetails.greetingMessage}
                onChange={(e) => handleInputChange('businessDetails', 'greetingMessage', e.target.value)}
                placeholder="e.g., Hello! How can I assist you today?"
                required
              />
            </InputGroup>
          </InputGroup>
        </Section>

        <Section>
          <SectionTitle>Additional Information</SectionTitle>
          <InputGroup>
            <Label>Expected Interaction Volume <Required>*</Required></Label>
            <Input
              type="text"
              value={formData.additionalInfo.interactionVolume}
              onChange={(e) => handleInputChange('additionalInfo', 'interactionVolume', e.target.value)}
              placeholder="E.g., 50-100 chats per day"
              required
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              Estimate how many conversations your AI assistant will handle (for example, "50-100 chats per day" or "~500 messages per week").
            </div>
          </InputGroup>
          <InputGroup>
            <Label>Additional Notes</Label>
            <TextArea
              value={formData.additionalInfo.notes}
              onChange={(e) => handleInputChange('additionalInfo', 'notes', e.target.value)}
              placeholder="Any additional notes or requests..."
            />
          </InputGroup>
        </Section>

        <Section>
          <SectionTitle>Marketplace Listing <span style={{ fontSize: 14, fontWeight: 400, opacity: 0.7 }}>(optional)</span></SectionTitle>
          <div style={{ marginBottom: '14px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: 1.5 }}>
            Visitors on imavenai.com describe what they need and see matching advisers. These details let them find
            and filter you. Intake stays free for the visitor; you can change all of this later in Settings → Marketplace.
          </div>
          <InputGroup>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!(formData.marketplace && formData.marketplace.listed)}
                onChange={(e) => setMarketplace({ listed: e.target.checked })}
              />
              <span>Show me on the iMaven marketplace once my account is approved</span>
            </label>
          </InputGroup>
          <InputGroup>
            <Label>Languages you consult in</Label>
            <CodePicker
              options={mpOptions.languages}
              value={(formData.marketplace && formData.marketplace.languages) || []}
              onChange={(languages) => setMarketplace({ languages })}
              placeholder="Search languages…"
            />
          </InputGroup>
          <InputGroup>
            <Label>Countries you serve</Label>
            <CodePicker
              options={mpOptions.countries}
              value={(formData.marketplace && formData.marketplace.countries) || []}
              onChange={(countries) => setMarketplace({ countries })}
              placeholder="Search countries…"
              quickPicks={0}
            />
          </InputGroup>
          <InputGroup>
            <Label>First consultation price (indicative)</Label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!(formData.marketplace && formData.marketplace.firstConsultation && formData.marketplace.firstConsultation.free)}
                  onChange={(e) => setMarketplaceFc({ free: e.target.checked })}
                />
                <span>Free</span>
              </label>
              {!(formData.marketplace && formData.marketplace.firstConsultation && formData.marketplace.firstConsultation.free) && (
                <>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    style={{ width: '140px' }}
                    value={(formData.marketplace && formData.marketplace.firstConsultation && formData.marketplace.firstConsultation.price) || ''}
                    onChange={(e) => setMarketplaceFc({ price: e.target.value })}
                    placeholder="e.g. 150"
                  />
                  <Select
                    style={{ width: '220px' }}
                    value={(formData.marketplace && formData.marketplace.firstConsultation && formData.marketplace.firstConsultation.currency) || 'USD'}
                    onChange={(e) => setMarketplaceFc({ currency: e.target.value })}
                  >
                    {(mpOptions.currencies.length ? mpOptions.currencies : [{ code: 'USD', label: 'US Dollar' }]).map(c => (
                      <option key={c.code} value={c.code}>{c.code} — {c.label}</option>
                    ))}
                  </Select>
                </>
              )}
            </div>
            <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              Shown on your marketplace card only. Nothing is charged through the marketplace.
            </div>
          </InputGroup>
        </Section>

        <Section>
          <SectionTitle>API Endpoint Key</SectionTitle>
          <InputGroup>
            <Label>Custom URL Key <Required>*</Required></Label>
            <Input
              type="text"
              value={formData.uniqueName}
              onChange={(e) => handleInputChange('uniqueName', 'uniqueName', e.target.value)}
              onBlur={checkNameOnBlur}
              placeholder="Enter a unique key for your API endpoint (e.g. my-company)"
              required
            />
            {uniqueNameStatus.isChecking ? (
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                Checking availability...
              </div>
            ) : uniqueNameStatus.message && (
              uniqueNameStatus.isAvailable ? (
                <SuccessMessage>{uniqueNameStatus.message}</SuccessMessage>
              ) : (
                <ErrorMessage>{uniqueNameStatus.message}</ErrorMessage>
              )
            )}
            <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              This key will be used in your chat widget URL: https://adviser.imavenai.com/chat/[your-key]
            </div>
          </InputGroup>
        </Section>

        <Section>
          <SectionTitle>Widget Customization</SectionTitle>
          <InputGroup>
            <Label>Color Scheme</Label>
            <Select
              value={formData.widgetCustomization.headerColor}
              onChange={e => handleInputChange('widgetCustomization', 'headerColor', e.target.value)}
            >
              {COLOR_SCHEME_OPTIONS.map(option => (
                <option key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</option>
              ))}
            </Select>
          </InputGroup>
          <InputGroup>
            <Label>Logo URL</Label>
            <Input
              type="url"
              value={formData.widgetCustomization.logoUrl}
              onChange={e => handleInputChange('widgetCustomization', 'logoUrl', e.target.value)}
              placeholder="https://example.com/logo.svg"
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              If left blank, the default logo will be used.
            </div>
          </InputGroup>
        </Section>

        <Section>
          <SectionTitle>{terms?.title || 'Expert Terms'} <Required>*</Required></SectionTitle>
          <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '10px' }}>
            Your account comes with your own AI agent, created from iMaven's starter knowledge for your field.
          </div>
          {terms ? (
            <>
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '14px', color: 'rgba(255, 255, 255, 0.85)', background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.15)', borderRadius: '10px', padding: '14px 16px' }}>
                {terms.text}
              </div>
              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '12px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ marginTop: '3px' }}
                />
                <span>I have read and accept these terms (version {terms.version}).</span>
              </label>
            </>
          ) : termsError ? (
            <ErrorMessage>
              Could not load the terms. <button type="button" onClick={loadTerms} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>Try again</button>
            </ErrorMessage>
          ) : (
            <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>Loading the terms…</div>
          )}
        </Section>

        <Section>
          <FormNote>
            Please ensure all required fields marked with <Required>*</Required> are completed before submission.
          </FormNote>
          <SubmitButton
            onClick={handleSubmit}
            disabled={!checkFormValidity() || submitting}>
            {submitting ? 'Submitting…' : 'Submit Onboarding Form'}
          </SubmitButton>
        </Section>
      </FormContainer>
    </PageWrapper>
  );
};

export default OnboardingForm; 